import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./ImageCarousel.css";
export default function ImageCarousel({ images, alt = "" }) {
  const slides = (images || []).filter(Boolean);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const trackRef = useRef(null);
  const goTo = useCallback(
    (next) => {
      if (slides.length === 0) return;
      setIndex(((next % slides.length) + slides.length) % slides.length);
    },
    [slides.length]
  );
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  useEffect(() => setIndex(0), [images]);
  function onKeyDown(e) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  }
  function onTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
  }
  function onTouchEnd(e) {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const SWIPE_THRESHOLD = 40;
    if (delta > SWIPE_THRESHOLD) prev();
    else if (delta < -SWIPE_THRESHOLD) next();
    touchStartX.current = null;
  }
  if (slides.length === 0) return null;
  if (slides.length === 1) {
    return (
      <div className="carousel carousel--single">
        <img src={slides[0]} alt={alt} className="carousel__img" loading="lazy" />
      </div>
    );
  }
  return (
    <div
      className="carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label={alt || "Image gallery"}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div
        className="carousel__viewport"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="carousel__track"
          ref={trackRef}
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((src, i) => (
            <div className="carousel__slide" key={src + i} aria-hidden={i !== index}>
              <img
                src={src}
                alt={alt ? `${alt} — image ${i + 1} of ${slides.length}` : `Image ${i + 1} of ${slides.length}`}
                className="carousel__img"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          className="carousel__nav carousel__nav--prev"
          onClick={prev}
          aria-label="Previous image"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          className="carousel__nav carousel__nav--next"
          onClick={next}
          aria-label="Next image"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="carousel__dots" role="tablist">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Go to image ${i + 1}`}
            className={`carousel__dot ${i === index ? "is-active" : ""}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </div>
  );
}